import React, { FC, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import AccordionSummary from '@mui/material/AccordionSummary';
import Accordion from '@mui/material/Accordion';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { TwAccordionDetails } from '../../../TwAccordion/TwAccordion';
import { ReactComponent as GenerateIcon } from '../../../../assets/icons/generate-ratecon.svg';
import { ReactComponent as PdfIcon } from '../../../../assets/icons/pdf-file.svg';
import { TwButton, TwButtonType } from '../../../TwButtons/TwButtons';
import { LoadDocumentType, LoadInvoiceResponse } from '../../../../redux/api/load/load.model';
import TwGenericMenu, { TwGenericMenuOptions } from '../../../TwGenericMenu/TwGenericMenu';
import LoadsService from '../../../../providers/Loads/Loads.service';
import { formatDistanceToNow } from 'date-fns';
import useNotifications from '../../../../providers/Notifications/Notifications.provider';
import './TwGenerateRateCon.scss';
import {
  setInvoiceDivisionId,
  setInvoiceLoadIds,
  setOrderId
} from '../../../../redux/features/orders/ordersSlice';
import {
  openCreateInvoice,
  openInvoicePreview
} from '../../../../redux/features/navigation/navigationSlice';
import { useAppDispatch } from '../../../../redux/hooks';
import TwGenerateInvoice from './TwGenerateInvoice';
import { OrderAttachment } from '../../../../redux/api/orders/orders.model';

interface TwGenerateDocsProps {
  files: OrderAttachment[];
  loadId: string;
  isBrokeredLoad: boolean;
  invoice: LoadInvoiceResponse | null;
  divisionId: string;
  onSuccessfulGenerate?: () => void;
  onClick?: (id: string, attachmentId: string) => void;
  onDelete: (file: OrderAttachment) => void;
  onDownload: (file: OrderAttachment) => void;
  onEditInvoice: () => void;
  onDeleteInvoice: () => void;
  handleSendToFactoring: (invoiceId: string | null) => void;
}

interface TwGenerateDocumentProps {
  doc: {
    file?: OrderAttachment;
    label: string;
    generate: () => Promise<Blob>;
    type: LoadDocumentType;
  };
  onSuccessfulGenerate?: () => void;
  onDelete: (file: OrderAttachment) => void;
  onDownload: (file: OrderAttachment) => void;
  viewDoc?: (id: string, attachmentId: string) => void;
}

const TwGenerateDocuments: FC<TwGenerateDocsProps> = ({
  files,
  loadId,
  isBrokeredLoad,
  invoice,
  divisionId,
  onSuccessfulGenerate,
  onClick,
  onDelete,
  onDownload,
  onEditInvoice,
  onDeleteInvoice,
  handleSendToFactoring
}): JSX.Element => {
  const dispatch = useAppDispatch();

  const generateRateCon = (): Promise<Blob> => {
    return LoadsService.generateRateConfirmationPdf(loadId);
  };

  const generateBol = (): Promise<Blob> => {
    return LoadsService.generateBolPdf(loadId);
  };

  const handleInvoicePDFOpen = (): void => {
    if (invoice) {
      loadId && dispatch(setOrderId(loadId));

      dispatch(openInvoicePreview());
    }
  };

  const documentsToGenerate = (): {
    label: string;
    file?: OrderAttachment;
    generate: () => Promise<Blob>;
    type: LoadDocumentType;
  }[] => {
    const documentsList = [
      {
        label: 'Bill of Lading',
        file: files.find((file) => file.type === LoadDocumentType.billOfLading),
        generate: generateBol,
        type: LoadDocumentType.billOfLading
      }
    ];

    // if isBrokeredLoad is true, add Rate Confirmation to the base array as first element
    if (isBrokeredLoad) {
      documentsList.unshift({
        label: 'Rate Confirmation',
        file: files.find((file) => file.type === LoadDocumentType.rateConfirmation),
        generate: generateRateCon,
        type: LoadDocumentType.rateConfirmation
      });
    }

    return documentsList;
  };

  const handleCreateInvoice = (): void => {
    dispatch(setInvoiceDivisionId(divisionId));
    dispatch(setInvoiceLoadIds([loadId]));
    dispatch(openCreateInvoice());
  };

  return (
    <Accordion defaultExpanded={true} className="generate-rate-con__root">
      <AccordionSummary className="generate-rate-con__summary" expandIcon={<ExpandMoreIcon />}>
        <Box component="div" className="load-detail__data-container__data--title">
          Generate Documents
        </Box>
      </AccordionSummary>
      <TwAccordionDetails className="generate-rate-con__details">
        {documentsToGenerate().map((doc, index) => (
          <TwGenerateDocument
            key={index}
            doc={doc}
            onSuccessfulGenerate={onSuccessfulGenerate}
            onDelete={onDelete}
            onDownload={onDownload}
            viewDoc={onClick}
          />
        ))}
        <TwGenerateInvoice
          invoice={invoice}
          loadId={loadId}
          handleClickView={handleInvoicePDFOpen}
          handleGenerateClick={handleCreateInvoice}
          handleEditInvoiceOpen={onEditInvoice}
          handleDeleteInvoice={onDeleteInvoice}
          handleSendToFactoring={handleSendToFactoring}
          reloadData={onSuccessfulGenerate}
        />
      </TwAccordionDetails>
    </Accordion>
  );
};

export default TwGenerateDocuments;

const TwGenerateDocument: FC<TwGenerateDocumentProps> = ({
  doc,
  onSuccessfulGenerate,
  onDelete,
  onDownload,
  viewDoc
}): JSX.Element => {
  const { addNotification } = useNotifications();
  const [loading, setLoading] = useState<boolean>(false);

  const handleGenerateClick = (): void => {
    setLoading(true);
    doc
      .generate()
      .then((res: any) => {
        onSuccessfulGenerate && onSuccessfulGenerate();
      })
      .catch((err: any) => {
        addNotification({
          variant: 'error',
          message: err.message || 'Error generating document, please try again'
        });
      })
      .finally(() => setLoading(false));
  };

  const showGenerateText = (type: LoadDocumentType): string => {
    switch (type) {
      case LoadDocumentType.rateConfirmation:
        return 'Generate your confirmation here';

      case LoadDocumentType.billOfLading:
        return 'Generate your bill of lading here';

      default:
        return 'Generate your document here';
    }
  };

  const options: TwGenericMenuOptions[] = [
    {
      label: 'Regenerate',
      action: handleGenerateClick
    },
    {
      label: 'Download',
      action: (): void => {
        doc.file && onDownload(doc.file);
      }
    },

    {
      label: 'Delete',
      className: 'detail-file__delete',
      action: (): void => {
        doc.file && onDelete(doc.file);
      }
    }
  ];

  const handleClickView = (): void => {
    doc.file && viewDoc && viewDoc(doc.file.id, doc.file.attachment.id);
  };

  return (
    <Box className="generate-rate-con__docs-container">
      <Stack
        flexDirection={'row'}
        onClick={handleClickView}
        className="generate-rate-con__info-section"
      >
        {doc.file?.is_generated ? <PdfIcon /> : <GenerateIcon />}
        <Box display="flex" flexDirection={'column'} sx={{ marginLeft: '15px' }}>
          <span className="detail-file__filename">{doc.label}</span>
          <span className="detail-file__text detail-file__info-gray">
            {doc.file && doc.file.is_generated
              ? `Generated • ${formatDistanceToNow(
                  doc.file.created_at ? new Date(doc.file.created_at) : new Date()
                )} ago`
              : showGenerateText(doc.type)}
          </span>
        </Box>
      </Stack>

      {!loading ? (
        doc.file && doc.file.is_generated ? (
          <TwGenericMenu className="generate-rate-con__menu" options={options} />
        ) : (
          <TwButton
            className="generate-rate-con__btn"
            type={TwButtonType.secondary}
            label="Generate"
            action={handleGenerateClick}
          />
        )
      ) : (
        <LoadingRateCon />
      )}
    </Box>
  );
};

const LoadingRateCon = (): JSX.Element => {
  return (
    <Stack flexDirection={'row'} alignItems={'center'} gap={1} className="generate-rate-con__btn">
      <span className="detail-file__text">Generating...</span>
      <CircularProgress size={30} />
    </Stack>
  );
};
